import authService from './authService';

// ─── Types ──────────────────────────────────────────────────────

export interface SalesReportFilters {
  date_from: string;
  date_to: string;
  order_status_ids?: number[];
  product_skus?: string[];
  seller_id?: number;
  order_type_ids?: number[];
  /** Empresas emissoras; vazio/ausente = todas. */
  company_ids?: number[];
  sales_channel_ids?: number[];
  payment_method_ids?: number[];
  is_payed?: boolean;
  is_first_purchase?: boolean;
  page: number;
  pageSize: number;
}

export interface SalesReportRow {
  order_id: number;
  order_num_erp: number;
  order_date: string;
  customer_name: string;
  order_status_id: number;
  order_status: string;
  ship_addr_city: string;
  ship_addr_state: string;
  order_products_total: number;
  shipping_price: number;
  order_discount: number;
  product_replacement_discount_value?: number;
  order_sales_target?: number;
  other_expenses: number;
  order_sell_total: number;
  is_first_purchase?: boolean | null;
  payment_method_name?: string;
  payment_method_erp_id?: number | null;
}

export interface SalesReportSummary {
  total_orders: number;
  total_products: number;
  total_shipping: number;
  total_discount: number;
  total_sales_target?: number;
  total_other_expenses: number;
  total_sell: number;
  average_ticket?: number;
}

export interface SalesReportResponse {
  data: SalesReportRow[];
  summary: SalesReportSummary;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ─── Products Report Types ──────────────────────────────────────

export interface SalesReportProductsFilters {
  date_from: string;
  date_to: string;
  order_status_ids?: number[];
  order_type_ids?: number[];
  /** Empresas emissoras; vazio/ausente = todas. */
  company_ids?: number[];
  is_payed?: boolean;
  page: number;
  pageSize: number;
}

export interface SalesReportProductRow {
  sku: string;
  description: string;
  quantity_sold: number;
  sale_value: number;
  discount_value: number;
  total_value: number;
}

export interface SalesReportProductsSummary {
  total_products: number;
  total_quantity: number;
  total_sale_value: number;
  total_discount_value: number;
  total_value: number;
}

export interface SalesReportProductsResponse {
  data: SalesReportProductRow[];
  summary: SalesReportProductsSummary;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ─── Sales Goal Report Types (Meta de Venda) ────────────────────

export interface SalesGoalMonthlyItem {
  year: number;
  month: number;
  sales_value: number;
  goal_value: number;
  has_goal: boolean;
  goal_met: boolean;
}

export interface SalesGoalMonthlyReport {
  items: SalesGoalMonthlyItem[];
}

export interface SalesGoalChannel {
  sales_channel_id: number;
  sales_channel_name: string;
}

export interface SalesGoalDailyItem {
  day: number;
  date: string;
  is_business_day: boolean;
  sales_value: number;
  goal: number;
  accumulated_sales: number;
  channel_values: Record<string, number>;
}

export interface SalesGoalDailyReport {
  year: number;
  month: number;
  monthly_goal: number;
  business_days: number;
  daily_goal: number;
  channels: SalesGoalChannel[];
  days: SalesGoalDailyItem[];
}

// ─── Service ────────────────────────────────────────────────────

class SalesReportService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }

  async getReport(filters: SalesReportFilters): Promise<SalesReportResponse> {
    const params = new URLSearchParams({
      page: filters.page.toString(),
      limit: filters.pageSize.toString(),
      date_from: filters.date_from,
      date_to: filters.date_to,
    });

    if (filters.order_status_ids && filters.order_status_ids.length > 0) {
      params.append('order_status_ids', filters.order_status_ids.join(','));
    }

    if (filters.product_skus && filters.product_skus.length > 0) {
      params.append('product_skus', filters.product_skus.join(','));
    }

    if (filters.seller_id !== undefined) {
      params.append('seller_id', filters.seller_id.toString());
    }

    if (filters.order_type_ids && filters.order_type_ids.length > 0) {
      params.append('order_type_ids', filters.order_type_ids.join(','));
    }
    if (filters.company_ids && filters.company_ids.length > 0) {
      params.append('company_ids', filters.company_ids.join(','));
    }

    if (filters.sales_channel_ids && filters.sales_channel_ids.length > 0) {
      params.append('sales_channel_ids', filters.sales_channel_ids.join(','));
    }

    if (filters.payment_method_ids && filters.payment_method_ids.length > 0) {
      params.append('payment_method_ids', filters.payment_method_ids.join(','));
    }

    if (filters.is_payed !== undefined) {
      params.append('is_payed', filters.is_payed.toString());
    }

    if (filters.is_first_purchase !== undefined) {
      params.append('is_first_purchase', filters.is_first_purchase.toString());
    }

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/sales?${params}`);
    if (!response.ok) throw new Error('Erro ao carregar relatório de vendas');

    const json = await response.json();

    const items: SalesReportRow[] = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};
    const summary: SalesReportSummary = json.summary ?? {
      total_orders: 0,
      total_products: 0,
      total_shipping: 0,
      total_discount: 0,
      total_other_expenses: 0,
      total_sell: 0,
    };

    return {
      data: items,
      summary,
      pagination: {
        page: pg.page ?? filters.page,
        limit: pg.page_size ?? pg.pageSize ?? filters.pageSize,
        total: pg.total_items ?? pg.total ?? items.length,
        totalPages: pg.total_pages ?? pg.totalPages ?? (Math.ceil((pg.total_items ?? pg.total ?? items.length) / filters.pageSize) || 1),
      },
    };
  }

  async downloadPdf(filters: Omit<SalesReportFilters, 'page' | 'pageSize'>): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const params = new URLSearchParams({
      date_from: filters.date_from,
      date_to: filters.date_to,
    });
    if (filters.order_status_ids && filters.order_status_ids.length > 0) {
      params.append('order_status_ids', filters.order_status_ids.join(','));
    }
    if (filters.product_skus && filters.product_skus.length > 0) {
      params.append('product_skus', filters.product_skus.join(','));
    }
    if (filters.seller_id !== undefined) {
      params.append('seller_id', filters.seller_id.toString());
    }
    if (filters.order_type_ids && filters.order_type_ids.length > 0) {
      params.append('order_type_ids', filters.order_type_ids.join(','));
    }
    if (filters.company_ids && filters.company_ids.length > 0) {
      params.append('company_ids', filters.company_ids.join(','));
    }
    if (filters.sales_channel_ids && filters.sales_channel_ids.length > 0) {
      params.append('sales_channel_ids', filters.sales_channel_ids.join(','));
    }
    if (filters.payment_method_ids && filters.payment_method_ids.length > 0) {
      params.append('payment_method_ids', filters.payment_method_ids.join(','));
    }
    if (filters.is_payed !== undefined) {
      params.append('is_payed', filters.is_payed.toString());
    }
    if (filters.is_first_purchase !== undefined) {
      params.append('is_first_purchase', filters.is_first_purchase.toString());
    }

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/reports/sales/pdf?${params}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar PDF (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `relatorio-vendas.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ─── Excel Export (backend) ───────────────────────────────────

  async downloadExcel(filters: Omit<SalesReportFilters, 'page' | 'pageSize'>): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const params = new URLSearchParams({
      date_from: filters.date_from,
      date_to: filters.date_to,
    });
    if (filters.order_status_ids && filters.order_status_ids.length > 0) {
      params.append('order_status_ids', filters.order_status_ids.join(','));
    }
    if (filters.product_skus && filters.product_skus.length > 0) {
      params.append('product_skus', filters.product_skus.join(','));
    }
    if (filters.seller_id !== undefined) {
      params.append('seller_id', filters.seller_id.toString());
    }
    if (filters.order_type_ids && filters.order_type_ids.length > 0) {
      params.append('order_type_ids', filters.order_type_ids.join(','));
    }
    if (filters.company_ids && filters.company_ids.length > 0) {
      params.append('company_ids', filters.company_ids.join(','));
    }
    if (filters.sales_channel_ids && filters.sales_channel_ids.length > 0) {
      params.append('sales_channel_ids', filters.sales_channel_ids.join(','));
    }
    if (filters.payment_method_ids && filters.payment_method_ids.length > 0) {
      params.append('payment_method_ids', filters.payment_method_ids.join(','));
    }
    if (filters.is_payed !== undefined) {
      params.append('is_payed', filters.is_payed.toString());
    }
    if (filters.is_first_purchase !== undefined) {
      params.append('is_first_purchase', filters.is_first_purchase.toString());
    }

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/reports/sales/excel?${params}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar Excel (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `relatorio-vendas.xlsx`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ─── Products Report ──────────────────────────────────────────

  async getProductsReport(filters: SalesReportProductsFilters): Promise<SalesReportProductsResponse> {
    const params = new URLSearchParams({
      page: filters.page.toString(),
      limit: filters.pageSize.toString(),
      date_from: filters.date_from,
      date_to: filters.date_to,
    });

    if (filters.order_status_ids && filters.order_status_ids.length > 0) {
      params.append('order_status_ids', filters.order_status_ids.join(','));
    }
    if (filters.order_type_ids && filters.order_type_ids.length > 0) {
      params.append('order_type_ids', filters.order_type_ids.join(','));
    }
    if (filters.company_ids && filters.company_ids.length > 0) {
      params.append('company_ids', filters.company_ids.join(','));
    }
    if (filters.is_payed !== undefined) {
      params.append('is_payed', filters.is_payed.toString());
    }

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/sales/products?${params}`);
    if (!response.ok) throw new Error('Erro ao carregar relatório de produtos vendidos');

    const json = await response.json();

    const items: SalesReportProductRow[] = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};
    const summary: SalesReportProductsSummary = json.summary ?? {
      total_products: 0,
      total_quantity: 0,
      total_sale_value: 0,
      total_discount_value: 0,
      total_value: 0,
    };

    return {
      data: items,
      summary,
      pagination: {
        page: pg.page ?? filters.page,
        limit: pg.page_size ?? pg.pageSize ?? filters.pageSize,
        total: pg.total_items ?? pg.total ?? items.length,
        totalPages: pg.total_pages ?? pg.totalPages ?? (Math.ceil((pg.total_items ?? pg.total ?? items.length) / filters.pageSize) || 1),
      },
    };
  }

  async downloadProductsPdf(filters: Omit<SalesReportProductsFilters, 'page' | 'pageSize'>): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const params = new URLSearchParams({
      date_from: filters.date_from,
      date_to: filters.date_to,
    });
    if (filters.order_status_ids && filters.order_status_ids.length > 0) {
      params.append('order_status_ids', filters.order_status_ids.join(','));
    }
    if (filters.order_type_ids && filters.order_type_ids.length > 0) {
      params.append('order_type_ids', filters.order_type_ids.join(','));
    }
    if (filters.company_ids && filters.company_ids.length > 0) {
      params.append('company_ids', filters.company_ids.join(','));
    }
    if (filters.is_payed !== undefined) {
      params.append('is_payed', filters.is_payed.toString());
    }

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/reports/sales/products/pdf?${params}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar PDF (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `relatorio-produtos-vendidos.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ─── Sales Goal Report (Meta de Venda) ────────────────────────

  /**
   * Month-by-month comparison of net sales value vs the registered sales goal.
   * date_from / date_to are "YYYY-MM". When omitted the backend defaults to the last 12 months.
   */
  async getGoalMonthlyReport(dateFrom?: string, dateTo?: string): Promise<SalesGoalMonthlyReport> {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);

    const apiClient = await this.getApiClient();
    const qs = params.toString();
    const response = await apiClient.get(`/reports/sales/goal/monthly${qs ? `?${qs}` : ''}`);
    if (!response.ok) throw new Error('Erro ao carregar relatório de meta de venda');

    const json = await response.json();
    const data = json.data ?? json;
    return { items: Array.isArray(data?.items) ? data.items : [] };
  }

  /**
   * Current-month daily report: daily sales, business-day-distributed daily goal, accumulated
   * sales and per-channel breakdown. When year/month are omitted the backend defaults to the
   * current month.
   */
  async getGoalDailyReport(year?: number, month?: number): Promise<SalesGoalDailyReport> {
    const params = new URLSearchParams();
    if (year !== undefined) params.append('year', year.toString());
    if (month !== undefined) params.append('month', month.toString());

    const apiClient = await this.getApiClient();
    const qs = params.toString();
    const response = await apiClient.get(`/reports/sales/goal/daily${qs ? `?${qs}` : ''}`);
    if (!response.ok) throw new Error('Erro ao carregar meta de venda diária');

    const json = await response.json();
    const data = (json.data ?? json) as SalesGoalDailyReport;
    return {
      year: data?.year ?? (year ?? new Date().getFullYear()),
      month: data?.month ?? (month ?? new Date().getMonth() + 1),
      monthly_goal: data?.monthly_goal ?? 0,
      business_days: data?.business_days ?? 0,
      daily_goal: data?.daily_goal ?? 0,
      channels: Array.isArray(data?.channels) ? data.channels : [],
      days: Array.isArray(data?.days) ? data.days : [],
    };
  }
}

export const salesReportService = new SalesReportService();
export default salesReportService;
